import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  ShieldCheckIcon,
  LockClosedIcon,
  ServerStackIcon,
  KeyIcon,
  DocumentCheckIcon,
  EyeSlashIcon,
  CloudIcon,
  WrenchScrewdriverIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  CheckBadgeIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import MarketingLayout from '../components/marketing/MarketingLayout';

/* ─── Animation Helpers ─── */
const ease = [0.44, 0, 0.56, 1] as const;

function FadeUp({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 32 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─── Data ─── */

const frameworks = [
  'NIST CSF 2.0',
  'CSA STAR',
  'GLBA Safeguards Rule',
  'FFIEC Guidance',
  'U.S. Treasury FS AI RMF',
];

const securityControls = [
  {
    icon: ServerStackIcon,
    title: 'Infrastructure',
    description:
      'US-hosted cloud infrastructure on SOC 2 Type II certified providers: Render (backend & PostgreSQL), Microsoft Azure (blob storage & Redis cache), Vercel (frontend CDN). No on-premises components.',
  },
  {
    icon: LockClosedIcon,
    title: 'Encryption',
    description:
      'TLS 1.2+ enforced on all endpoints with HSTS preload. AES-256 encryption at rest across all storage layers. Passwords hashed with bcrypt (12 rounds). API keys and OAuth tokens stored as SHA-256 hashes only.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Multi-tenancy Isolation',
    description:
      'Every database query includes teamId filter enforced at the ORM layer. File storage uses team-scoped blob paths. No administrative super-user endpoint bypasses tenant isolation.',
  },
  {
    icon: KeyIcon,
    title: 'Authentication & Access',
    description:
      'Five-role RBAC with explicit permission sets and fail-closed design. OAuth 2.0 with PKCE for integrations. MFA on all infrastructure accounts. No shared credentials or default passwords.',
  },
  {
    icon: DocumentCheckIcon,
    title: 'Audit Trail',
    description:
      'Immutable, append-only audit log with SHA-256 hash chain and monotonic sequence numbers. 26+ distinct event types. Nightly integrity verification with immediate alert on chain break.',
  },
  {
    icon: EyeSlashIcon,
    title: 'Minimal Data Footprint',
    description:
      'Processes marketing content and HMDA data only. AI providers receive analysis content with no user identifiers or institution names. Never accesses consumer PII, account data, or core banking systems.',
  },
  {
    icon: CloudIcon,
    title: 'MCP & Copilot Integration',
    description:
      'OAuth 2.0 with PKCE (S256) for all MCP connections. Only submitted content passes through the integration layer — no tenant data accessed or stored by connected AI assistants.',
  },
  {
    icon: WrenchScrewdriverIcon,
    title: 'Application Security',
    description:
      'Security headers via helmet.js and Vercel (CSP, HSTS, X-Frame-Options, Permissions-Policy). Per-endpoint rate limiting. Zod schema validation on all API inputs. Dependabot dependency scanning.',
  },
  {
    icon: ArrowPathIcon,
    title: 'Business Continuity',
    description:
      'RTO: 4 hours, RPO: 24 hours across 7 documented disaster scenarios. Three-provider AI failover with circuit breaker. Daily automated database backups with 7-day retention. Alternative hosting providers identified.',
  },
];

const roadmapItems = [
  {
    label: 'NIST CSF 2.0 Self-Assessment',
    status: 'Complete',
    date: 'March 2026',
    complete: true,
  },
  {
    label: 'CSA STAR Self-Assessment',
    status: 'Complete',
    date: 'March 2026',
    complete: true,
  },
  {
    label: 'Penetration Testing',
    status: 'Planned',
    date: 'Q2 2026',
    complete: false,
  },
  {
    label: 'SOC 2 Type I',
    status: 'Planned',
    date: 'Q3–Q4 2026',
    complete: false,
  },
  {
    label: 'SOC 2 Type II',
    status: 'Planned',
    date: '2027',
    complete: false,
  },
];

const securityDocs = [
  {
    id: 'CLARID-SEC-001',
    title: 'Written Information Security Program (WISP)',
  },
  { id: 'CLARID-RA-001', title: 'NIST CSF 2.0 Risk Assessment' },
  { id: 'CLARID-CSA-001', title: 'CSA STAR CAIQ v4 Self-Assessment' },
  { id: 'CLARID-BCP-001', title: 'Business Continuity Plan' },
  { id: 'CLARID-AI-001', title: 'AI Governance Policy' },
  { id: 'CLARID-VEN-001', title: 'Vendor Inventory' },
  { id: 'CLARID-DD-002', title: 'Due Diligence Package' },
];

/* ─── Page ─── */

export default function SecurityPage() {
  return (
    <MarketingLayout>

      {/* ━━━ HERO ━━━ */}
      <section className="section" style={{ textAlign: 'center' }}>
        <div className="container">
          <FadeUp>
            <span className="kicker" style={{ justifyContent: 'center' }}>
              <ShieldCheckIcon aria-hidden="true" style={{ width: '0.95rem', height: '0.95rem' }} />
              Security · Effective March 25, 2026
            </span>
            <h1 className="display balance" style={{ marginTop: '1.5rem' }}>Security &amp; Trust</h1>
            <p className="lead" style={{ margin: '1.5rem auto 0', maxWidth: '54ch' }}>
              Built for regulated financial institutions. Every layer of Clarid is designed to
              protect your data, meet examiner expectations, and earn the trust of your
              compliance team.
            </p>
          </FadeUp>
        </div>
      </section>

      {/* ━━━ FRAMEWORK ALIGNMENT ━━━ */}
      <section className="section-tight" style={{ textAlign: 'center', paddingTop: 0 }}>
        <div className="container">
          <FadeUp>
            <p className="kicker kicker--ink" style={{ justifyContent: 'center', marginBottom: '1.75rem' }}>
              Framework Alignment
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {frameworks.map((fw) => (
                <span
                  key={fw}
                  className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-white px-4 py-2 text-sm font-medium text-navy-900 shadow-sm"
                >
                  <CheckBadgeIcon className="h-4 w-4 text-teal-500" />
                  {fw}
                </span>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ━━━ SECURITY CONTROLS GRID ━━━ */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-7xl 2xl:max-w-[1400px] px-6 lg:px-8">
          <FadeUp className="text-center mb-16">
            <p className="text-sm font-semibold text-teal-600 uppercase tracking-widest font-mono">
              How We Protect Your Data
            </p>
            <h2 className="mt-3 text-3xl sm:text-5xl font-display font-light text-navy-900">
              Security controls
            </h2>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityControls.map((control, idx) => {
              const Icon = control.icon;
              return (
                <FadeUp key={control.title} delay={idx * 0.05}>
                  <div className="h-full bg-[#FAFAF9] rounded-2xl p-6 border border-gray-200/80">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-teal-50">
                        <Icon className="h-5 w-5 text-teal-600" />
                      </div>
                      <h3 className="text-base font-semibold text-navy-900">
                        {control.title}
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {control.description}
                    </p>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ━━━ COMPLIANCE ROADMAP ━━━ */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-4xl 2xl:max-w-5xl px-6 lg:px-8">
          <FadeUp className="text-center mb-12">
            <p className="text-sm font-semibold text-teal-600 uppercase tracking-widest font-mono">
              Progress
            </p>
            <h2 className="mt-3 text-3xl sm:text-5xl font-display font-light text-navy-900">
              Compliance roadmap
            </h2>
          </FadeUp>

          <FadeUp>
            <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
              {roadmapItems.map((item, idx) => (
                <div
                  key={item.label}
                  className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 px-6 py-5 ${
                    idx < roadmapItems.length - 1
                      ? 'border-b border-gray-100'
                      : ''
                  }`}
                >
                  <span className="text-sm font-semibold text-navy-900 sm:flex-1">
                    {item.label}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">{item.date}</span>
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        item.complete
                          ? 'bg-teal-50 text-teal-700 border border-teal-200'
                          : 'bg-gray-100 text-slate-600 border border-gray-200'
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ━━━ DOCUMENTATION AVAILABLE ━━━ */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="mx-auto max-w-4xl 2xl:max-w-5xl px-6 lg:px-8">
          <FadeUp className="text-center mb-12">
            <p className="text-sm font-semibold text-teal-600 uppercase tracking-widest font-mono">
              Transparency
            </p>
            <h2 className="mt-3 text-3xl sm:text-5xl font-display font-light text-navy-900">
              Documentation available on request
            </h2>
            <p className="mt-5 text-lg text-slate-500 max-w-2xl mx-auto">
              We share our security documentation with prospective customers,
              auditors, and regulators. Contact{' '}
              <a
                href="mailto:security@clarid.ai"
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                security@clarid.ai
              </a>{' '}
              to request any of the following.
            </p>
          </FadeUp>

          <FadeUp>
            <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-[#FAFAF9] shadow-sm">
              {securityDocs.map((doc, idx) => (
                <div
                  key={doc.id}
                  className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-6 py-4 ${
                    idx < securityDocs.length - 1
                      ? 'border-b border-gray-200/60'
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-3 sm:flex-1">
                    <DocumentTextIcon className="h-5 w-5 text-teal-500 flex-shrink-0" />
                    <span className="text-sm font-medium text-navy-900">
                      {doc.title}
                    </span>
                  </div>
                  <span className="text-xs text-slate-400 font-mono pl-8 sm:pl-0">
                    {doc.id}
                  </span>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ━━━ REPORT A VULNERABILITY ━━━ */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-4xl 2xl:max-w-5xl px-6 lg:px-8">
          <FadeUp>
            <div className="bg-navy-900 rounded-3xl px-8 py-16 sm:px-16 sm:py-20 text-center">
              <EnvelopeIcon className="mx-auto h-10 w-10 text-teal-400 mb-6" />
              <h2 className="text-3xl sm:text-4xl font-display font-light text-white leading-tight">
                Report a vulnerability
              </h2>
              <p className="mt-5 text-lg text-slate-400 max-w-xl mx-auto">
                If you discover a security issue, please report it to{' '}
                <a
                  href="mailto:security@clarid.ai"
                  className="text-teal-400 hover:text-teal-300 font-medium"
                >
                  security@clarid.ai
                </a>
                . We take all reports seriously and will respond within 24
                hours.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

    </MarketingLayout>
  );
}
